import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaSearch, 
  FaTimes, 
  FaUserPlus, 
  FaCheck,
  FaHeart,
  FaStar
} from 'react-icons/fa';
import api from '../../services/api';
import { fixMediaUrl } from '../../utils/imageHelpers';
import './NewConversationModal.css';



const NewConversationModal = ({ isOpen, onClose, onCreateConversation, existingConversations = [] }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creating, setCreating] = useState(false);

  // Fetch loves when modal opens
  useEffect(() => {
    const fetchLoves = async () => {
      if (!isOpen) return;
      
      setLoading(true);
      try {
        // Use loves endpoint to get mutual loves
        const response = await api.get('/loves');
        console.log('Loves response:', response.data);
        
        // Filter out users that already have conversations
        const existingIds = existingConversations.map(conv => 
          conv.participant?.id || conv.participant_id
        );
        
        // Transform loves to user format - handle multiple possible data structures
        const loves = response.data?.data || response.data || [];
        
        const filteredUsers = loves
          .map(love => {
            // Try to find the other user (not the current user)
            const currentUserId = love.user_id || love.sender_id;
            const otherUser = love.receiver || love.user || love.other_user || love;
            
            // Extract profile info from various possible paths
            const profile = otherUser.profile || otherUser.user?.profile || love.profile || {};
            
            // Handle avatar URL - use fixMediaUrl for consistency with rest of app
            let avatar = profile.avatar || otherUser.avatar || otherUser.photo || profile.photo || love.avatar || love.photo || otherUser.photo_url || profile.photo_url;
            avatar = fixMediaUrl(avatar) || '/default-avatar.svg';

            
            return {
              id: otherUser.id || love.receiver_id || love.user_id || love.id,

              name: profile.name || otherUser.name || otherUser.username || otherUser.email?.split('@')[0] || 'Utilisateur',
              avatar: avatar || '/default-avatar.svg',
              is_online: otherUser.is_online || false,
              is_verified: otherUser.is_verified || profile.is_verified || false,
              city: profile.city || otherUser.city || profile.location || otherUser.location,
              distance: love.distance || otherUser.distance,
              common_interests: love.common_interests || otherUser.common_interests || 0,
            };
          })

          .filter(user => user.id && !existingIds.includes(user.id));
        
        console.log('Filtered users:', filteredUsers);
        setUsers(filteredUsers);
      } catch (error) {
        console.error('Error fetching loves:', error);
        // Fallback: try love-requests endpoint
        try {
          const lrResponse = await api.get('/love-requests?status=accepted');
          console.log('Love requests response:', lrResponse.data);
          
          const existingIds = existingConversations.map(conv => 
            conv.participant?.id || conv.participant_id
          );
          
          const loveRequests = lrResponse.data?.data || lrResponse.data || [];
          const filteredUsers = loveRequests
            .map(lr => {
              // Get the other user (receiver or sender)
              const otherUser = lr.receiver || lr.sender || lr.user || lr;
              const profile = otherUser.profile || {};
              
              // Handle avatar URL - use fixMediaUrl for consistency with rest of app
              let avatar = profile.avatar || otherUser.avatar || otherUser.photo || profile.photo || lr.avatar || lr.photo || otherUser.photo_url || profile.photo_url;
              avatar = fixMediaUrl(avatar) || '/default-avatar.svg';


              
              return {
                id: otherUser.id,
                name: profile.name || otherUser.name || otherUser.username || otherUser.email?.split('@')[0] || 'Utilisateur',
                avatar: avatar || '/default-avatar.svg',
                is_online: otherUser.is_online || false,
                is_verified: otherUser.is_verified || profile.is_verified || false,
                city: profile.city || otherUser.city || profile.location || otherUser.location,
                distance: lr.distance || otherUser.distance,
                common_interests: lr.common_interests || otherUser.common_interests || 0,
              };
            })

            .filter(user => user.id && !existingIds.includes(user.id));
            
          console.log('Filtered users from love-requests:', filteredUsers);
          setUsers(filteredUsers);
        } catch (fallbackError) {
          console.error('Error fetching love-requests:', fallbackError);
          setUsers([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLoves();
  }, [isOpen, existingConversations]);



  // Filter users locally based on search query
  const filteredUsers = users.filter(user => {
    if (!searchQuery || searchQuery.length < 2) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.name?.toLowerCase().includes(query) ||
      user.city?.toLowerCase().includes(query)
    );
  });


  const handleCreateConversation = async () => {
    if (!selectedUser) return;

    setCreating(true);
    try {
      await onCreateConversation(selectedUser.id);
      onClose();
      setSearchQuery('');
      setSelectedUser(null);
      setUsers([]);
    } catch (error) {
      console.error('Error creating conversation:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    onClose();
    setSearchQuery('');
    setSelectedUser(null);
    setUsers([]);
  };

  const getCompatibilityScore = (user) => {
    // Calculate compatibility based on common interests, location, etc.
    const commonInterests = user.common_interests || 0;
    const nearby = user.distance && user.distance < 10;
    
    let score = 50; // Base score
    score += commonInterests * 10;
    if (nearby) score += 20;
    if (user.is_verified) score += 10;
    
    return Math.min(score, 100);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            className="new-conversation-modal"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            {/* Header */}
            <div className="modal-header">
              <div className="modal-title">
                <FaUserPlus className="title-icon" />
                <h3>Nouvelle Conversation</h3>
              </div>
              <button className="close-btn" onClick={handleClose}>
                <FaTimes />
              </button>
            </div>

            {/* Search */}
            <div className="modal-search">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Rechercher un utilisateur..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchQuery && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchQuery('')}
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {/* Content */}
            <div className="modal-content">
              {loading ? (
                <div className="search-loading">
                  <div className="spinner-small" />
                  <p>Chargement de vos loves...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="no-results">
                  <div className="no-results-icon">💕</div>
                  <p>Aucun love mutuel disponible</p>
                  <span>Allez dans Loves pour envoyer des coeurs !</span>
                </div>

              ) : filteredUsers.length === 0 ? (
                <div className="no-results">
                  <div className="no-results-icon">🔍</div>
                  <p>Aucun résultat pour "{searchQuery}"</p>
                  <span>Essayez avec d'autres termes de recherche</span>
                </div>
              ) : (
                <div className="users-list">
                  {filteredUsers.map((user) => {

                    const compatibility = getCompatibilityScore(user);
                    const isSelected = selectedUser?.id === user.id;

                    return (
                      <motion.div
                        key={user.id}
                        className={`user-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => setSelectedUser(user)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        layout
                      >
                        <div className="user-avatar">
                          <img 
                            src={user.avatar || '/default-avatar.svg'} 
                            alt={user.name}
                            onError={(e) => {
                              e.target.src = '/default-avatar.svg';
                            }}
                          />
                          {user.is_online && <span className="online-dot" />}
                          {user.is_verified && (
                            <span className="verified-badge">
                              <FaCheck />
                            </span>
                          )}
                        </div>

                        <div className="user-info">
                          <div className="user-header">
                            <h4>{user.name}</h4>
                            {compatibility >= 70 && (
                              <span className="compatibility-badge high">
                                <FaHeart /> {compatibility}%
                              </span>
                            )}
                          </div>
                          
                          <p className="user-location">
                            {user.city || 'Localisation inconnue'}
                            {user.distance && ` • ${Math.round(user.distance)} km`}
                          </p>

                          {user.common_interests > 0 && (
                            <p className="common-interests">
                              <FaStar /> {user.common_interests} intérêts communs
                            </p>
                          )}
                        </div>

                        {isSelected && (
                          <motion.div 
                            className="selected-indicator"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            <FaCheck />
                          </motion.div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button 
                className="btn-cancel"
                onClick={handleClose}
              >
                Annuler
              </button>
              <button
                className="btn-create"
                onClick={handleCreateConversation}
                disabled={!selectedUser || creating}
              >
                {creating ? (
                  <>
                    <div className="spinner-tiny" />
                    Création...
                  </>
                ) : (
                  <>
                    <FaUserPlus />
                    Démarrer la conversation
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NewConversationModal;
